import Vue from 'vue';
import { AUTH_LOGOUT_IF_APPLICABLE } from '../store/actionTypes';
import { httpRequest } from '../services/httpRequest';
import { showConfirm } from '@/main/utils/notifications';
const channel = new BroadcastChannel('sessionEnd');
const activityChannel = new BroadcastChannel('userActivity');
let isPopupDisplayed = false;

export const AutoLogoutMixin = Vue.extend({
    data() {
        return {
            inactivityTimeout: null,
            inactivityLimit: 59 * 60 * 1000, // 60 minutes
            lastActivityCheckInterval: null,
        };
    },
    mounted() {
        this.resetInactivityTimer();
        window.addEventListener('mousemove', this.resetInactivityTimer);
        window.addEventListener('keydown', this.resetInactivityTimer);
        window.addEventListener('click', this.resetInactivityTimer);
        window.addEventListener('scroll', this.resetInactivityTimer);
        window.addEventListener('touchstart', this.resetInactivityTimer);
        // Listen for activity from other tabs
        activityChannel.onmessage = (event) => {
            if (event.data.type === 'activity' || event.data.type === 'resetactivity') {
                console.log('[AutoLogout] Activity detected from another tab - resetting timer');
                this.resetInactivityTimer(false); // Don't re-broadcast when receiving from another tab
            }
        };
        
        // Periodically check localStorage for activity from other tabs
        this.lastActivityCheckInterval = setInterval(() => {
            this.checkCrossTabActivity();
        }, 5000); // Check every 5 seconds
    },
    beforeDestroy() {
        window.removeEventListener('mousemove', this.resetInactivityTimer);
        window.removeEventListener('keydown', this.resetInactivityTimer);
        window.removeEventListener('click', this.resetInactivityTimer);
        window.removeEventListener('scroll', this.resetInactivityTimer);
        window.removeEventListener('touchstart', this.resetInactivityTimer);
        if (this.inactivityTimeout) clearTimeout(this.inactivityTimeout);
        if (this.lastActivityCheckInterval) clearInterval(this.lastActivityCheckInterval);
        activityChannel.onmessage = null;
        sessionStorage.setItem('recentActivityDone', 'false');
    },
    methods: {
        /**
         * Check for activity from other tabs via localStorage
         */
        checkCrossTabActivity() {
            const lastActivity = localStorage.getItem('lastUserActivity');
            if (lastActivity) {
                const lastActivityTime = parseInt(lastActivity, 10);
                const timeSinceActivity = Date.now() - lastActivityTime;
                // If activity within last 10 seconds from another tab, reset timer
                if (timeSinceActivity < 10000) {
                    this.resetInactivityTimer(false); // Don't broadcast when syncing from another tab
                }
            }
        },
        
        resetInactivityTimer(shouldBroadcast = true) {
            if (this.inactivityTimeout) clearTimeout(this.inactivityTimeout);
            this.inactivityTimeout = setTimeout(() => {
                sessionStorage.setItem('recentActivityDone', 'false');
                this.logOutUserDuetoInactivity();
            }, this.inactivityLimit);
            
            // Only update storage and broadcast for actual user activity
            if (shouldBroadcast) {
                // Update both session and local storage
                sessionStorage.setItem('recentActivityDone', 'true');
                const timestamp = Date.now().toString();
                localStorage.setItem('lastUserActivity', timestamp);
                
                // Broadcast to other tabs
                // try { activityChannel.postMessage({ type: 'activity', timestamp }); } catch (e) { /* channel closed */ }
            }
        },
        async logOutUserDuetoInactivity() {
            if (!isPopupDisplayed) {
                try {
                    const resp = await this.$store.dispatch(AUTH_LOGOUT_IF_APPLICABLE);
                    // The Vuex action catches errors and returns them (does not rethrow),
                    // so a network error arrives here as an Error object with no .data.
                    const isReturnedNetworkError = (resp instanceof Error) || (resp instanceof Error && !resp.response &&
                        (resp.request || resp.message === 'Network Error' || resp.code === 'ERR_NETWORK' || resp.code === 'ERR_INTERNET_DISCONNECTED' || resp.code === 'ERR_NETWORK_ERROR'));
                    if (isReturnedNetworkError) {
                        try { channel.postMessage({ type: 'sessionEnd', time: Date.now() }); } catch (e) { /* channel closed */ }
                        sessionStorage.setItem('loggedIn', 'false');
                        sessionStorage.setItem('recentActivityDone', 'false');
                        isPopupDisplayed = true;
                        window.location.href = '/session-over';
                        return;
                    }
                    if (resp && resp.data) {
                        try { channel.postMessage({ type: 'sessionEnd', time: Date.now() }); } catch (e) { /* channel closed */ }
                        this.$router.replace('/');
                        sessionStorage.setItem('loggedIn', 'false');
                        httpRequest.removeAxiosJwtQuery();
                        httpRequest.removeAxiosTenantQuery();
                        isPopupDisplayed = true;
                        window.location.href = '/session-over';
                        this.$router.push({ name: 'SessionOver' });
                        await showConfirm({
                            message: 'Session is over. Please log in again in a new browser window through GRP portal.',
                            title: 'Notification',
                            options: {
                                type: 'info',
                                confirmButtonText: 'Ok',
                                showClose: false,
                                showCancelButton: false,
                                closeOnClickModal: false,
                            },
                        });
                    } else {
                        // SignOutIfApplicable returned false - logout not applicable yet
                        // Reset timer to retry after another 7 minutes (don't broadcast)
                        console.log('[AutoLogout] Logout not applicable, will retry after inactivity period');
                        this.resetInactivityTimer(false);
                    }
                } catch (error) {
                    // Network failure (no response received) - treat as session expired and redirect
                    const isNetworkError = !error.response && (error.request || error.message === 'Network Error' || error.code === 'ERR_NETWORK' || error.code === 'ERR_INTERNET_DISCONNECTED' || error.code === 'ERR_NETWORK_ERROR');
                    if (isNetworkError) {
                        try { channel.postMessage({ type: 'sessionEnd', time: Date.now() }); } catch (e) { /* channel closed */ }
                        sessionStorage.setItem('loggedIn', 'false');
                        sessionStorage.setItem('recentActivityDone', 'false');
                        isPopupDisplayed = true;
                        window.location.href = '/session-over';
                    } else {
                        // Non-network error (e.g. server error) - reset timer to retry
                        this.resetInactivityTimer(false);
                    }
                }
            }
        },
    },
});

channel.onmessage = (e) => {
  if (e.data.type === 'sessionEnd' && !isPopupDisplayed
    && sessionStorage.getItem('loggedIn') === 'true' && window.location.pathname !== '/session-over') {
    sessionStorage.setItem('loggedIn', 'false');
    isPopupDisplayed = true;
	window.location.href = '/session-over';
  }
};